import { Indexed, isString, last, rangeFrom, shift } from '../../utils';
import { NBSP } from '../constants';
import { Day, Month } from '../types';
import { Calendar, WeekdayKey, WEEKDAYS } from './types';

export class WeekdayMap {
  private content: number[] = [0, 1, 2, 3, 4, 5, 6];
  private weekDays: WeekdayKey[];

  constructor(weekStartsOn: WeekdayKey) {
    const offset = WEEKDAYS.indexOf(weekStartsOn);
    // Rotate weekdays to correct order
    shift(this.content, -offset);
    this.weekDays = shift([...WEEKDAYS], offset);
  }

  getIndex(day: number): number {
    const index: number = day >= 0 ? day : 7 + (day % 7);
    return this.content[index % 7];
  }

  weekdayKeys(): WeekdayKey[] {
    return this.weekDays;
  }
}

export const asDate = (day: Day | string): Date => new Date(`${isString(day) ? day : day.id}T00:00:00.000Z`);
export const asDateString = (date: Date): string =>
  date instanceof Date && !isNaN(date.getDate()) ? date.toISOString().substring(0, 10) : '';
const weekday = (day: Day, weekdayMap: WeekdayMap): number => weekdayMap.getIndex(asDate(day).getUTCDay());

export const createCalendar = (timestamp: string, numberOfDays: number, weekStartsOn: WeekdayKey): Calendar => {
  const dateIterator: Date = new Date(timestamp);
  const asDay = (date: Date): Day => ({ id: asDateString(date), value: date.getUTCDate().toString() });
  const prevDay = (day: Day): Day => {
    const date = asDate(day);
    date.setUTCDate(date.getUTCDate() - 1);
    return asDay(date);
  };

  const weekdayMap: WeekdayMap = new WeekdayMap(weekStartsOn);

  const monthsAsObject: Indexed<Day[]> = rangeFrom(1, numberOfDays)
    .map(() => {
      const day: Day = asDay(dateIterator);
      dateIterator.setUTCDate(dateIterator.getUTCDate() + 1);
      return day;
    })
    .reduce((acc: Indexed<Day[]>, day: Day) => {
      const month: string = getMonth(day.id);
      acc[month] = (acc[month] || []).concat(day);
      return acc;
    }, {});

  const months: Month[] = Object.keys(monthsAsObject).reduce((acc: Month[], id: string) => {
    let days: Day[] = monthsAsObject[id];

    const firstDay: Day = days[0];
    const weekdayOfFirstDay = weekday(firstDay, weekdayMap);
    if (weekdayOfFirstDay > 0) {
      const fillerBaseId = `${prevDay(firstDay).id}-pre`;
      const fillers: Day[] = rangeFrom(1, weekdayOfFirstDay).map((i: number) => ({
        id: `${fillerBaseId}-${i}`,
        value: NBSP,
        fill: true,
      }));
      days = [...fillers, ...days];
    }

    const lastDay: Day = last(days);
    const weekdayOfLastDay = weekday(lastDay, weekdayMap);
    if (weekdayOfLastDay < 6) {
      const fillerBaseId = `${lastDay.id}-post`;
      const fillers: Day[] = rangeFrom(1, 6 - weekdayOfLastDay).map((i: number) => ({
        id: `${fillerBaseId}-${i}`,
        value: NBSP,
        fill: true,
      }));
      days = [...days, ...fillers];
    }

    return acc.concat({ id, days });
  }, []);

  return { months, numberOfDays, weekStartsOn, weekdayKeys: weekdayMap.weekdayKeys() };
};

export const getMonth = (date: string): string => {
  return date.substring(0, 7); // 2018-10-12 -> 2018-10
};

export const startDayOfLastWeekOfMonth = (month: Month, weekStartsOn: WeekdayKey): Day => {
  const days: Day[] = month.days.filter((d) => !d.fill);
  const weekdayMap: WeekdayMap = new WeekdayMap(weekStartsOn);
  return days[days.length - 1 - weekday(last(days), weekdayMap)];
};

export const daysBetween = (from: string, to: string): number =>
  Math.abs(new Date(from).getTime() - new Date(to).getTime()) / (1000 * 60 * 60 * 24);
